import { isArray, isBoolean } from 'lodash';
import { traverseTree, traverseTrees } from '@/utils/tree-kit';
import { IImageEffectShadowType, ImgCategoryType, ImgModeType, LayerType, MarkupType, MaskType } from '@/version-types';
import { getDefaultBackgroundValue, getDefaultTextGradient } from '../constants';
import {
    IServerBgLayerProps,
    IServerImageLayerProps,
    IServerLayerProps,
    IServerMarkupLayerProps,
    IServerTextLayerProps,
    IServerVectorLayerProps,
    IServerWatermarkLayerProps,
} from '../server-template-types/layer';
import { VERSION } from '../versions';

/**
 * 将图层升级到最新版本
 *
 * @param layers - 图层列表
 * @param stageWidth - 宽度
 * @param stageHeight - 高度
 * @param version - 版本
 */
export const parseLayersToLatestVersion = (
    layers: Partial<IServerLayerProps>[],
    stageWidth: number,
    stageHeight: number,
    version: VERSION,
) => {
    // 历史原因取消6.1.0版本 这里做个兼容
    if ((version as any) === '6.1.0') {
        version = VERSION['6.0.0'];
    }

    if (!parseNextVersionMap[version]) {
        return layers as IServerLayerProps[];
    }

    const latestLayers = parseNextVersionMap[version](layers, stageWidth, stageHeight);

    return complementLayersData(latestLayers) as IServerLayerProps[];
};

const parseV1ToV2 = (layers: Partial<IServerLayerProps>[], stageWidth: number, stageHeight: number) => {
    const hasBgLayers = layers.find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER);

    if (!hasBgLayers) {
        layers.unshift(getDefaultBackgroundValue(stageWidth, stageHeight));
    }

    parseNextVersionMap[VERSION['2.0.0']](layers);
    return layers;
};

const parseV2ToV2_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers = layers.map((layer, i) => {
        if (layer.hidden === undefined) {
            layer.hidden = false;
        }
        return layer;
    });

    parseNextVersionMap[VERSION['2.0.1']](layers);

    return layers;
};

const parseV2_1ToV3 = (layers: Partial<IServerLayerProps>[]) => {
    layers = layers.map((layer, i) => {
        const newLayer = layer as IServerImageLayerProps;
        /** 解决历史bug */
        if (newLayer.layerType === LayerType.IMAGE_LAYER) {
            if (newLayer.maskURL === undefined) {
                newLayer.maskURL = '';
            }
            if (newLayer.maskType === undefined) {
                newLayer.maskType = MaskType.none;
            }
            if (newLayer.mode === undefined) {
                newLayer.mode = ImgModeType.none;
            }
            if (newLayer.category === undefined) {
                newLayer.category = ImgCategoryType.material;
            }

            layer = {
                ...layer,
                maskURL: '',
                maskType: MaskType.none,
                mode: ImgModeType.none,
                category: ImgCategoryType.material,
            };
        }
        return newLayer;
    });

    parseNextVersionMap[VERSION['3.0.0']](layers);

    return layers;
};

const parseV3ToV3_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((layer: Partial<IServerLayerProps>) => {
        if (layer.layerType === LayerType.IMAGE_LAYER && !(layer as IServerImageLayerProps).cutoutMaskURL) {
            (layer as IServerImageLayerProps).cutoutMaskURL = '';
        }
    });
    parseNextVersionMap[VERSION['3.1.0']](layers);

    return layers;
};

const parseV3_1ToV4 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((layer: Partial<IServerLayerProps>) => {
        layer.parentId = '';
        layer.children = [];
        layer.title = '';
    });

    parseNextVersionMap[VERSION['4.0.0']](layers);
    return layers;
};

const parseV4ToV4_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                const { textGlow, textShadow, textStroke } = layer as IServerTextLayerProps;

                textGlow.forEach((grow) => {
                    grow.hidden = false;
                });

                textShadow.forEach((shadow) => {
                    shadow.hidden = false;
                });

                textStroke.forEach((stroke) => {
                    stroke.hidden = false;
                });
            }
        });
    });

    parseNextVersionMap[VERSION['4.1.0']](layers);

    return layers;
};

const parseV4_1ToV5 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            layer.materialIds = {};
        });
    });

    parseNextVersionMap[VERSION['5.0.0']](layers);
    return layers;
};

const parseV5ToV5_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                (layer as IServerImageLayerProps).filter = null;
            }
        });
    });
    parseNextVersionMap[VERSION['5.1.0']](layers);
    return layers;
};

const parseV5_1ToV5_2 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['5.2.0']](layers);
    return layers;
};

const parseV5_2ToV6 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['6.0.0']](layers);
    return layers;
};

const parseV6ToV7 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['7.0.0']](layers);
    return layers;
};

const parseV7ToV7_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                (layer as IServerImageLayerProps).effect = null;
            }
        });
    });

    parseNextVersionMap[VERSION['7.1.0']](layers);
    return layers;
};

const parseV7_1ToV8 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['8.0.0']](layers);
    return layers;
};

const parseV8ToV8_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                (layer as IServerTextLayerProps).textGradient = getDefaultTextGradient();
            }
        });
    });

    parseNextVersionMap[VERSION['8.1.0']](layers);
    return layers;
};

const parseV8_1ToV8_2 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                (layer as IServerBgLayerProps).backgroundGradient = null;
            }
        });
    });
    parseNextVersionMap[VERSION['8.2.0']](layers);
    return layers;
};

const parseV8_2ToV8_3 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                (layer as IServerTextLayerProps).textBackground = [];
            }
        });
    });

    parseNextVersionMap[VERSION['8.3.0']](layers);
    return layers;
};

const parseV8_3To8_4 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                (layer as IServerTextLayerProps).listStyle = '';
            }
        });
    });

    parseNextVersionMap[VERSION['8.4.0']](layers);
    return layers;
};

const parseV8_4To8_5 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['8.5.0']](layers);
    return layers;
};

const parseV8_5To9_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['9.0.0']](layers);
    return layers;
};

const parseV9To9_0_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['9.0.1']](layers);
    return layers;
};

const parse9_0_1To10 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['10.0.0']](layers);
    return layers;
};

const parse10_To11 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['11.0.0']](layers);
    return layers;
};

const parse11_To11_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            (layer as IServerBgLayerProps).isAILayer = false;
        });
    });
    parseNextVersionMap[VERSION['11.1.0']](layers);
    return layers;
};

const parse11_1_To12 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['12.0.0']](layers);
    return layers;
};

const parse12_To12_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['12.0.1']](layers);
    return layers;
};

const parse12_1To12_2 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['12.0.2']](layers);
    return layers;
};

const parse12_2To13 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['13.0.0']](layers);
    return layers;
};

const parse13To13_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            (layer as IServerTextLayerProps).textContents = [];
        });
    });
    parseNextVersionMap[VERSION['13.0.1']](layers);
    return layers;
};
const parse13_1To13_2 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['13.0.2']](layers);
    return layers;
};

const parse13_2To13_3 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['13.0.3']](layers);
    return layers;
};

const parse13_3To13_1_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['13.1.0']](layers);
    return layers;
};

const parse13_1_0To14 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                (layer as IServerImageLayerProps).aiTextExtension = null;
            }
        });
    });

    parseNextVersionMap[VERSION['14.0.0']](layers);
    return layers;
};

const parse14To14_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                (layer as IServerTextLayerProps).shapeExtension = null;
                (layer as IServerTextLayerProps).screenshotUrl = null;
            }
        });
    });

    parseNextVersionMap[VERSION['14.1.0']](layers);
    return layers;
};

const parse14_1To14_2 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                (layer as IServerImageLayerProps).effect = null;
            }
        });
    });
    parseNextVersionMap[VERSION['14.2.0']](layers);
    return layers;
};

const parse14_2To15 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['15.0.0']](layers);
    return layers;
};

const parse15To15_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            layer.fitWidth = 0;
            layer.fitHeight = 0;
        });
    });
    parseNextVersionMap[VERSION['15.1.0']](layers);
    return layers;
};

const parse15_1To15_1_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['15.1.1']](layers);
    return layers;
};

const parse15_1_1To15_1_2 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['15.1.2']](layers);
    return layers;
};

const parse15_1_2To15_2 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['15.2.0']](layers);
    return layers;
};

const parse15_2_0To15_3 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['15.3.0']](layers);
    return layers;
};

const parse15_3To16_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['16.0.0']](layers);
    return layers;
};

const parse16_0To17_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['17.0.0']](layers);
    return layers;
};

const parse17_0To17_0_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['17.0.1']](layers);
    return layers;
};

const parse17_0_1To17_0_2 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['17.0.2']](layers);
    return layers;
};

const parse17_0_2To17_0_3 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                const textBackground = (layer as IServerTextLayerProps).textBackground;
                if (textBackground?.length) {
                    textBackground.forEach((item) => {
                        item.roundWeight = item.roundWeight || 0;
                    });
                }
            }
        });
    });
    parseNextVersionMap[VERSION['17.0.3']](layers);
    return layers;
};
const parse17_0_3To17_1_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['17.1.0']](layers);
    return layers;
};

const parse17_1_0To17_1_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['17.1.1']](layers);
    return layers;
};

const parse17_1_1To17_1_2 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['17.1.2']](layers);
    return layers;
};

const parse17_1_2To17_2_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['17.2.0']](layers);
    return layers;
};

const parse17_2_0To18_0_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.0.0']](layers);
    return layers;
};

const parse18_0_0To18_0_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.0.1']](layers);
    return layers;
};
const parse18_0_1To18_0_2 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.VECTOR_LAYER) {
                const _layer = layer as IServerVectorLayerProps;
                _layer.blur = _layer.blur ?? 0;
                if (!isBoolean(_layer.strokeHidden)) {
                    _layer.strokeHidden = false;
                }
                if (!_layer.effect) {
                    _layer.effect = {
                        shadow: [],
                        reflection: [],
                    };
                }
            }
        });
    });
    parseNextVersionMap[VERSION['18.0.2']](layers);
    return layers;
};

const parse18_0_2To18_1_0 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerMarkupLayerProps;
            if (layer.layerType === LayerType.MARKUP_LAYER && _layer.markupType !== MarkupType.Segment) {
                _layer.anchor.points = [];
            }
        });
    });
    parseNextVersionMap[VERSION['18.1.0']](layers);
    return layers;
};

const parse18_1_0To18_1_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.1.1']](layers);
    return layers;
};

const parse18_1_1To18_2_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.2.0']](layers);
    return layers;
};

const parse18_2_0To18_2_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.2.1']](layers);
    return layers;
};
const parse18_2_1To18_2_2 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            const _layer = layer as IServerVectorLayerProps;
            if (layer.layerType === LayerType.VECTOR_LAYER && !_layer.borderRadius) {
                const radius = _layer.radius;
                _layer.borderRadius = {
                    leftTop: radius,
                    rightTop: radius,
                    leftBottom: radius,
                    rightBottom: radius,
                };
            }
        });
    });
    parseNextVersionMap[VERSION['18.2.2']](layers);
    return layers;
};
const parse18_2_2To18_2_3 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.2.3']](layers);
    return layers;
};

const parse18_2_3To18_4_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.4.0']](layers);
    return layers;
};

const parse18_4_0To18_5_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.5.0']](layers);
    return layers;
};

const parse18_5_0To18_6_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.6.0']](layers);
    return layers;
};

const parse18_6_0To18_7_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['18.7.0']](layers);
    return layers;
};

const parse18_7_0To19_0_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['19.0.0']](layers);
    return layers;
};

const parse19_0_0To19_1_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['19.1.0']](layers);
    return layers;
};

const parse19_1_0To19_2_0 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.IMAGE_LAYER) {
                const _layer = layer as IServerImageLayerProps;
                if (_layer.effect && _layer.effect?.shadow?.length > 0) {
                    _layer.effect.shadow.forEach((item) => {
                        item.type = IImageEffectShadowType.base;
                        item.rotate = 0;
                    });
                }
            }
        });
    });
    parseNextVersionMap[VERSION['19.2.0']](layers);
    return layers;
};

const parse19_2_0To19_3_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['19.3.0']](layers);
    return layers;
};

const parse19_3_0To19_4_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['19.4.0']](layers);
    return layers;
};

const parse19_4_0To20_0_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.0.0']](layers);
    return layers;
};

const parse20_0_0To20_0_1 = (layers: Partial<IServerLayerProps>[]) => {
    layers.forEach((topLayer: Partial<IServerLayerProps>) => {
        traverseTree(topLayer, (layer: Partial<IServerLayerProps>) => {
            if (layer.layerType === LayerType.WATERMARK) {
                const _layer = layer as IServerWatermarkLayerProps;
                _layer.isColorApplyToAll = false;
            }
        });
    });
    parseNextVersionMap[VERSION['20.0.1']](layers);
    return layers;
};

const parse20_0_1To20_0_2 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.0.2']](layers);
    return layers;
};

const parse20_0_2To20_0_3 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.0.3']](layers);
    return layers;
};

const parse20_0_3To20_0_4 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.0.4']](layers);
    return layers;
};

const parse20_0_4To20_1_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.1.0']](layers);
    return layers;
};

const parse20_1_0To20_1_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.1.1']](layers);
    return layers;
};

const parse20_1_1To20_2_0 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.2.0']](layers);
    return layers;
};

const parse20_2_0To20_2_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.2.1']](layers);
    return layers;
};

const parse20_2_1To20_3_1 = (layers: Partial<IServerLayerProps>[]) => {
    parseNextVersionMap[VERSION['20.3.1']](layers);
    return layers;
};

const parse20_3_1 = (layers: Partial<IServerLayerProps>[]) => {
    return layers;
};

const parseNextVersionMap = {
    [VERSION['1.0.0']]: parseV1ToV2,
    [VERSION['2.0.0']]: parseV2ToV2_1,
    [VERSION['2.0.1']]: parseV2_1ToV3,
    [VERSION['3.0.0']]: parseV3ToV3_1,
    [VERSION['3.1.0']]: parseV3_1ToV4,
    [VERSION['4.0.0']]: parseV4ToV4_1,
    [VERSION['4.1.0']]: parseV4_1ToV5,
    [VERSION['5.0.0']]: parseV5ToV5_1,
    [VERSION['5.1.0']]: parseV5_1ToV5_2,
    [VERSION['5.2.0']]: parseV5_2ToV6,
    [VERSION['6.0.0']]: parseV6ToV7,
    [VERSION['7.0.0']]: parseV7ToV7_1,
    [VERSION['7.1.0']]: parseV7_1ToV8,
    [VERSION['8.0.0']]: parseV8ToV8_1,
    [VERSION['8.1.0']]: parseV8_1ToV8_2,
    [VERSION['8.2.0']]: parseV8_2ToV8_3,
    [VERSION['8.3.0']]: parseV8_3To8_4,
    [VERSION['8.4.0']]: parseV8_4To8_5,
    [VERSION['8.5.0']]: parseV8_5To9_0,
    [VERSION['9.0.0']]: parseV9To9_0_1,
    [VERSION['9.0.1']]: parse9_0_1To10,
    [VERSION['10.0.0']]: parse10_To11,
    [VERSION['11.0.0']]: parse11_To11_1,
    [VERSION['11.1.0']]: parse11_1_To12,
    [VERSION['12.0.0']]: parse12_To12_1,
    [VERSION['12.0.1']]: parse12_1To12_2,
    [VERSION['12.0.2']]: parse12_2To13,
    [VERSION['13.0.0']]: parse13To13_1,
    [VERSION['13.0.1']]: parse13_1To13_2,
    [VERSION['13.0.2']]: parse13_2To13_3,
    [VERSION['13.0.3']]: parse13_3To13_1_0,
    [VERSION['13.1.0']]: parse13_1_0To14,
    [VERSION['14.0.0']]: parse14To14_1,
    [VERSION['14.1.0']]: parse14_1To14_2,
    [VERSION['14.2.0']]: parse14_2To15,
    [VERSION['15.0.0']]: parse15To15_1,
    [VERSION['15.1.0']]: parse15_1To15_1_1,
    [VERSION['15.1.1']]: parse15_1_1To15_1_2,
    [VERSION['15.1.2']]: parse15_1_2To15_2,
    [VERSION['15.2.0']]: parse15_2_0To15_3,
    [VERSION['15.3.0']]: parse15_3To16_0,
    [VERSION['16.0.0']]: parse16_0To17_0,
    [VERSION['17.0.0']]: parse17_0To17_0_1,
    [VERSION['17.0.1']]: parse17_0_1To17_0_2,
    [VERSION['17.0.2']]: parse17_0_2To17_0_3,
    [VERSION['17.0.3']]: parse17_0_3To17_1_0,
    [VERSION['17.1.0']]: parse17_1_0To17_1_1,
    [VERSION['17.1.1']]: parse17_1_1To17_1_2,
    [VERSION['17.1.2']]: parse17_1_2To17_2_0,
    [VERSION['17.2.0']]: parse17_2_0To18_0_0,
    [VERSION['18.0.0']]: parse18_0_0To18_0_1,
    [VERSION['18.0.1']]: parse18_0_1To18_0_2,
    [VERSION['18.0.2']]: parse18_0_2To18_1_0,
    [VERSION['18.1.0']]: parse18_1_0To18_1_1,
    [VERSION['18.1.1']]: parse18_1_1To18_2_0,
    [VERSION['18.2.0']]: parse18_2_0To18_2_1,
    [VERSION['18.2.1']]: parse18_2_1To18_2_2,
    [VERSION['18.2.2']]: parse18_2_2To18_2_3,
    [VERSION['18.2.3']]: parse18_2_3To18_4_0,
    [VERSION['18.4.0']]: parse18_4_0To18_5_0,
    [VERSION['18.5.0']]: parse18_5_0To18_6_0,
    [VERSION['18.6.0']]: parse18_6_0To18_7_0,
    [VERSION['18.7.0']]: parse18_7_0To19_0_0,
    [VERSION['19.0.0']]: parse19_0_0To19_1_0,
    [VERSION['19.1.0']]: parse19_1_0To19_2_0,
    [VERSION['19.2.0']]: parse19_2_0To19_3_0,
    [VERSION['19.3.0']]: parse19_3_0To19_4_0,
    [VERSION['19.4.0']]: parse19_4_0To20_0_0,
    [VERSION['20.0.0']]: parse20_0_0To20_0_1,
    [VERSION['20.0.1']]: parse20_0_1To20_0_2,
    [VERSION['20.0.2']]: parse20_0_2To20_0_3,
    [VERSION['20.0.3']]: parse20_0_3To20_0_4,
    [VERSION['20.0.4']]: parse20_0_4To20_1_0,
    [VERSION['20.1.0']]: parse20_1_0To20_1_1,
    [VERSION['20.1.1']]: parse20_1_1To20_2_0,
    [VERSION['20.2.0']]: parse20_2_0To20_2_1,
    [VERSION['20.2.1']]: parse20_2_1To20_3_1,
    [VERSION['20.3.1']]: parse20_3_1,
};

/**
 * 补全字段
 *
 * 1. 定义 `string` 字段可能返回为 `null` 需要改成空字符串
 * 2. 返回数据可能不存在 lock 字段 默认为 `false`
 *
 * @param layers - 图层数据
 */
const complementLayersData = (layers: Partial<IServerLayerProps>[]): Partial<IServerLayerProps>[] => {
    layers.forEach((layer: any, index: number) => {
        for (const key in layer) {
            if (!layer[key] && key === 'lock') {
                layer[key] = false;
            }

            if (layer[key] === null || layer[key] === undefined) {
                if (key === 'textMark') continue;
                layer[key] = '';
            }
        }
    });

    // 兼容下旧的脏数据
    traverseTrees(layers, (layer: Partial<IServerLayerProps>) => {
        if ((layer as any).hidden === undefined) {
            (layer as any).hidden = false;
        }
        if (layer.layerType === LayerType.IMAGE_LAYER) {
            if ((layer as any).effect === '' || (layer as any).effect === undefined) {
                (layer as any).effect = null;
            }

            if ((layer as any).filter === '' || (layer as any).filter === undefined) {
                (layer as any).filter = null;
            }

            if ((layer as any).cutoutMaskURL === undefined) {
                (layer as any).cutoutMaskURL = '';
            }
            if ((layer as any).maskType === undefined) {
                (layer as any).maskType = 1;
            }

            if ((layer as any).maskURL === undefined) {
                (layer as any).maskURL = '';
            }

            if ((layer as any).category === undefined) {
                (layer as any).category = ImgCategoryType.none;
            }

            if ((layer as any).mode === undefined) {
                (layer as any).mode = ImgModeType.none;
            }
        }

        if (layer.layerType === LayerType.TEXT_LAYER) {
            if (
                (layer as any).textGradient === '' ||
                (layer as any).textGradient === undefined ||
                isArray((layer as any).textGradient)
            ) {
                (layer as any).textGradient = getDefaultTextGradient();
            }

            if ((layer as any).textBackground === '' || (layer as any).textBackground === undefined) {
                (layer as any).textBackground = [];
            }

            if ((layer as IServerTextLayerProps).textShadow && (layer as IServerTextLayerProps).textShadow.length > 0) {
                (layer as any).textShadow = (layer as any).textShadow.map((item) => {
                    delete item.distance;
                    delete item.angle;
                    return item;
                });
            }
        }

        if (layer.layerType === LayerType.GROUP_LAYER && (layer as any).materialIds === undefined) {
            (layer as any).materialIds = {};
        }

        if (layer.layerType === LayerType.GROUP_LAYER && (layer as any).title === undefined) {
            (layer as any).title = '';
        }

        if (layer.layerType === LayerType.BACKGROUND_LAYER) {
            if ((layer as any).backgroundGradient === '' || (layer as any).backgroundGradient === undefined) {
                (layer as any).backgroundGradient = null;
            }

            if ((layer as any).title === undefined) {
                (layer as any).title = '';
            }

            if ((layer as any).color === undefined) {
                (layer as any).color = '';
            }
        }

        if (!layer.children) {
            layer.children = [];
        }
    });
    return layers;
};
